import { useCallback } from 'react';
import Field from './Field';
import SearchableSelect from './SearchableSelect';
import { api } from '../api/client';

/**
 * State, then the cities in it.
 *
 * <p>City used to be free text, which produced "Bengaluru", "Bangalore" and
 * "bangalore " in one column and no way to group by any of them. It is a master row
 * now — but the master will never hold every village, so the list ends in **Other**,
 * which reveals a box to type in. The common case becomes a row; the long tail is
 * still expressible.
 *
 * <p>The order is the point: there are thousands of cities and nobody can pick from
 * that. Choosing the state first narrows the list to something a person can read, and
 * the city field says so rather than sitting there empty and inert.
 *
 * <p>Both registration forms use this, and a shared component is what keeps the rule
 * — clear the city when the state changes — from being right in one of them and
 * forgotten in the other.
 */

/** The sentinel the list ends with. A string, so it can never collide with a row id. */
export const OTHER_CITY = 'OTHER';

const OTHER_OPTION = { id: OTHER_CITY, name: 'Other — not in the list' };

export const EMPTY_ADDRESS = { state: null, city: null, cityName: '' };

/** Whether the typed box is showing, which is also whether `cityName` is what counts. */
export const isOtherCity = (address) => address?.city?.id === OTHER_CITY;

/**
 * The three fields as the API wants them. Exactly one of `cityId` and `city` is sent,
 * which is exactly what the server stores.
 */
export function addressPayload(address) {
  const other = isOtherCity(address);
  return {
    stateId: address?.state?.id ?? null,
    cityId: other ? null : address?.city?.id ?? null,
    city: other ? address.cityName.trim() || null : null,
  };
}

export default function StateCityFields({ value, onChange, cityError }) {
  const address = value ?? EMPTY_ADDRESS;
  const stateId = address.state?.id ?? null;

  const searchStates = useCallback((q) => api.lookupStates(q), []);

  // Keyed on the state, so choosing a different one loads that state's cities rather
  // than filtering the old ones. Memoised, or the picker restarts its debounced search
  // on every keystroke elsewhere in the form.
  const searchCities = useCallback(
    (q) =>
      stateId == null
        ? Promise.resolve([])
        : api
            .lookupCities({ stateId, q, limit: 30 })
            .then((rows) => [...rows.map((row) => ({ id: row.id, name: row.name })), OTHER_OPTION]),
    [stateId],
  );

  // A city belongs to a state, so changing the state cannot leave the old city sitting
  // there — it would be submitted, and it would be in the wrong state.
  const chooseState = (state) => onChange({ state, city: null, cityName: '' });

  return (
    <>
      <div className="grid-2">
        <Field label="State" id="state">
          <SearchableSelect
            id="state"
            value={address.state}
            onChange={chooseState}
            loadOptions={searchStates}
            placeholder="Search states"
          />
        </Field>
        <Field
          label="City"
          id="city"
          hint={stateId == null
            ? 'The cities of the state you choose'
            : 'Not listed? Pick “Other” and type it'}
          error={cityError}
        >
          <SearchableSelect
            id="city"
            value={address.city}
            onChange={(city) => onChange({ ...address, city, cityName: '' })}
            loadOptions={searchCities}
            disabled={stateId == null}
            placeholder={stateId == null ? 'Choose a state first' : 'Search cities'}
            emptyLabel="No city matches — pick “Other”"
          />
        </Field>
      </div>

      {isOtherCity(address) && (
        <Field label="City name" id="cityName" required hint="The town or village, as you would write it">
          <input
            id="cityName"
            name="cityName"
            className="input"
            maxLength={120}
            value={address.cityName}
            onChange={(event) => onChange({ ...address, cityName: event.target.value })}
          />
        </Field>
      )}
    </>
  );
}
